import React from "react";
import { Button, Space, Tag, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import type { RoadmapItem } from "../../types/roadmap";
import { roadmapStatusLabel, roadmapStatusTagColor } from "./roadmapStatusConfig";

const { Text } = Typography;

const RowWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  min-height: 40px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  background: #fff;
  &:hover {
    background: #fafafa;
  }
`;

interface RoadmapCompactRowProps {
  item: RoadmapItem;
  onOpen: () => void;
  onTogglePriority: (e: React.MouseEvent) => void;
  officialUnlocked: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: (e: React.MouseEvent) => void;
  onMoveDown: (e: React.MouseEvent) => void;
}

export const RoadmapCompactRow: React.FC<RoadmapCompactRowProps> = ({
  item,
  onOpen,
  onTogglePriority,
  officialUnlocked,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
}) => {
  const subtasks = item.roadmap_subtasks ?? [];
  const doneCount = subtasks.filter((s) => s.done).length;
  const descPreview =
    item.description.length > 100 ? `${item.description.slice(0, 100)}…` : item.description;

  const finish = item.expected_finish_at ? dayjs(item.expected_finish_at) : null;
  const itemOverdue =
    finish &&
    item.status !== "done" &&
    item.status !== "wont_do" &&
    finish.startOf("day").isBefore(dayjs().startOf("day"));

  return (
    <RowWrap
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <Button
        type="text"
        size="small"
        aria-label={item.is_prioritized ? "Unprioritise" : "Prioritise"}
        icon={
          item.is_prioritized ? <StarFilled style={{ color: "#faad14" }} /> : <StarOutlined />
        }
        onClick={onTogglePriority}
      />
      <Tag color={roadmapStatusTagColor(item.status)} style={{ margin: 0, flexShrink: 0 }}>
        {roadmapStatusLabel(item.status)}
      </Tag>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong ellipsis style={{ display: "block" }}>
          {item.title}
        </Text>
        {descPreview ? (
          <Text type="secondary" ellipsis style={{ display: "block", fontSize: 12 }}>
            {descPreview}
          </Text>
        ) : null}
      </div>
      {finish && (
        <Text
          type={itemOverdue ? "danger" : "secondary"}
          style={{ fontSize: 12, flexShrink: 0 }}
        >
          {finish.format("D MMM YYYY")}
        </Text>
      )}
      {itemOverdue && (
        <Tag color="red" style={{ margin: 0, flexShrink: 0 }}>
          Overdue
        </Tag>
      )}
      {subtasks.length > 0 && (
        <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
          {doneCount}/{subtasks.length} sub
        </Text>
      )}
      {officialUnlocked && (
        <Space size={0} onClick={(e) => e.stopPropagation()}>
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={!canMoveUp}
            onClick={onMoveUp}
            aria-label="Move up"
          />
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={!canMoveDown}
            onClick={onMoveDown}
            aria-label="Move down"
          />
        </Space>
      )}
    </RowWrap>
  );
};
